import { Key, useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { TFunction, useTranslation } from 'react-i18next';
import { Spin, Table, TablePaginationConfig } from 'antd';
import { isNil } from 'lodash';
import { getAPISorter, getCellValue, getShowTotal } from 'utils/tableUtils';
import { getFormattedDate } from 'utils/dateUtils';
import styles from './MinimumGuaranteedCodesTab.module.less';
import api, { MgCodeDto, MgCodeListSortField } from 'api';
import { SorterResult, SortOrder } from 'antd/lib/table/interface';

type SorterType = SorterResult<MgCodeDto> | SorterResult<MgCodeDto>[];

const DefaultSorter = {
	order: 'descend' as SortOrder,
	column: {
		APISortField: MgCodeListSortField.CreatedAt,
	} as any,
};

const getColumns = (t: TFunction, sorterRef: SorterResult<MgCodeDto>) => [
	{
		title: t('redemptionCodesPage.Table.Code'),
		key: 'code',
		render: (record: MgCodeDto) => getCellValue(record.code),
		sorter: true,
		sortOrder: getSortOrder(sorterRef, MgCodeListSortField.Code),
		APISortField: MgCodeListSortField.Code,
	},
	{
		title: t('redemptionCodesPage.Table.Created'),
		key: 'createdDate',
		render: (record: MgCodeDto) =>
			getCellValue(
				getFormattedDate(record.createdDate, 'MM/DD/YYYY hh:mm:ss A'),
			),
		sorter: true,
		sortOrder: getSortOrder(sorterRef, MgCodeListSortField.CreatedAt),
		APISortField: MgCodeListSortField.CreatedAt,
	},
	{
		title: t('redemptionCodesPage.Table.Redeemed'),
		key: 'redeemedAt',
		render: (record: MgCodeDto) =>
			getCellValue(
				getFormattedDate(record.redeemedAt, 'MM/DD/YYYY hh:mm:ss A'),
			),
		sorter: true,
		sortOrder: getSortOrder(sorterRef, MgCodeListSortField.Redeemed),
		APISortField: MgCodeListSortField.Redeemed,
	},
	{
		title: t('redemptionCodesPage.Table.Player'),
		key: 'userName',
		render: (record: MgCodeDto) => <span>{getCellValue(record.userName)}</span>,
		sorter: true,
		sortOrder: getSortOrder(sorterRef, MgCodeListSortField.UserName),
		APISortField: MgCodeListSortField.UserName,
	},
];

const getSortOrder = (
	sorterRef: SorterResult<MgCodeDto>,
	field: MgCodeListSortField,
) => {
	return (sorterRef?.column as any)?.APISortField === field
		? sorterRef.order
		: undefined;
};

interface Props {
	productId: string;
	setSelectedRowsCallBackFunction: (param: MgCodeDto[]) => void;
	setHasNoRedemptionCodeBackFunction: (param: boolean) => void;
	resetAllowRefetchData: () => void;
	allowRefetchData: boolean;
	setSorterRefForExporting: (sorter: SorterType) => void;
	isActiveTab: boolean;
}

export default function MinimumGuaranteedCodesTab(props: Props) {
	const {
		productId,
		setSelectedRowsCallBackFunction,
		resetAllowRefetchData,
		allowRefetchData,
		setSorterRefForExporting,
		setHasNoRedemptionCodeBackFunction,
		isActiveTab
	} = props;
	const { t } = useTranslation();
	const showTotal = useCallback(
		(total: number, range: [number, number]) => getShowTotal(total, range, t),
		[t],
	);
	const [pagination, setPagination] = useState<TablePaginationConfig>({
		current: 1,
		pageSize: 10,
		size: 'default',
		showQuickJumper: true,
		showTotal,
		showSizeChanger: true,
	});
	const [sorterRef, setSorterRef] = useState<SorterType>(DefaultSorter);
	const columns = useMemo(
		() => getColumns(t, sorterRef as SorterResult<MgCodeDto>),
		[t, sorterRef],
	);

	const [tableData, setTableData] = useState<MgCodeDto[]>([]);
	const [hasNoRedemptionCode, setHasNoRedemptionCode] = useState(true);
	const [isLoading, setIsLoading] = useState(true);
	const [tableIsLoading, setTableIsLoading] = useState(false);
	const [, setSelectedRowKeys] = useState<Key[]>([]);

	const refetchData = async () => {
		setPagination({ ...pagination, current: 1 });
		await handleTableChange({ ...pagination, current: 1 }, null, DefaultSorter);
		resetAllowRefetchData();
	};

	useEffect(() => {
		if (allowRefetchData) {
			refetchData();
		}
	}, [allowRefetchData]);

	useEffect(() => {
		const getChanges = async () => {
			setIsLoading(true);
			await handleTableChange(
				{ ...pagination, current: 1 },
				null,
				DefaultSorter,
			);
			setIsLoading(false);
		};
		getChanges();
	}, [productId]);

	useEffect(() => {
		setHasNoRedemptionCodeBackFunction(isActiveTab && !tableData.length);
	}, [isActiveTab, tableData]);

	useEffect(() => {
		if (!isActiveTab) {
			setSelectedRowKeys([]);
			setSelectedRowsCallBackFunction([]);
		}
	}, [isActiveTab]);

	const handleTableChange = async (
		tablePagination: TablePaginationConfig,
		filters: any,
		sorter: SorterType,
	) => {
		setTableIsLoading(true);
		setSorterRef(sorter);
		setSorterRefForExporting(sorter);

		try {
			const res = await api.queryMgCodesByContentProvider(
				getAPISorter(sorter).sortField,
				getAPISorter(sorter).sortOrder,
				productId,
				undefined,
				tablePagination.current || 1,
				tablePagination.pageSize as number,
			);

			setTableData(res.items || []);
			setPagination({
				...pagination,
				...tablePagination,
				total: res.totalCount || 0,
			});
			setHasNoRedemptionCode(!!(!res.totalCount && tablePagination.current));
		} finally {
			setTableIsLoading(false);
		}
	};

	const renderTable = () => {
		if (!hasNoRedemptionCode) {
			return (
				<Table
					className="table"
					size="middle"
					columns={columns}
					rowKey={(record) => record.id || ('' as string)}
					dataSource={tableData}
					pagination={pagination}
					loading={tableIsLoading}
					onChange={handleTableChange}
					tableLayout="fixed"
					sortDirections={['descend', 'ascend', 'descend']}
					data-testid="minimumGuaranteedCodesTable"
				/>
			);
		} else {
			return (
				<div className={styles.emptyList} data-testid="noCodesDiv">
					<h2 className="h3">
						{t(
							'redemptionCodesPage.noData.description.minimumGuaranteedCode.line1',
						)}
					</h2>
				</div>
			);
		}
	};

	return <div>{isLoading ? <Spin /> : renderTable()}</div>;
}
